import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as session from 'smg/customer-api/session';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    if (await session.Redirect(undefined, Relref('/login'))) {
        return;
    }

    const b = new Batch();

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            if (lineItem.id) {
                m.set(lineItem.id, lineItem);
            }
        }
        return m;
    });

    void b.Exec();

    const pl: PageLoader<models.Quote> = new PageLoader<models.Quote>({
        get: () => axios.get<hyejoo.Quotes>('/v2/quotes', {
            params: {
                'sort': pl.Sort(),
                'max': pl.CurrentPageSize(),
                'data': 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key',
                'p': pl.CurrentPage(),
                'q': pl.Search(),
            },
        }).then(resp => ({ rows: resp.data.quotes, nextPage: resp.data.nextPage })),
        draw: async quote => {
            const el = template.Clone('#quote-template');

            const firstChild = el.firstElementChild;
            if (firstChild) {
                firstChild.id = quote.id || '';
            }

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const date = el.querySelector('.quote-date') as HTMLElement;
            date.textContent = DateTime.fromISO(quote.created || '').toLocaleString(DateTime.DATE_SHORT);

            const total = Big(quote.cachedTotal || 0);
            const shipping = Big(0);

            const subtotal = total.sub(shipping);

            const subtotalEls = el.querySelectorAll('.quote-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.quote-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const totalEls = el.querySelectorAll('.quote-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            if (quote.quoteLineItems?.length) {
                const qtys: Record<string, number> = {};
                for (const quoteLineItem of quote.quoteLineItems) {
                    if (!quoteLineItem.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(quoteLineItem.lineItemID || '');

                    if (!(quoteLineItem.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
                }

                const summary = el.querySelector('.quote-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const projectContainer = el.querySelector('.quote-project-container') as HTMLElement;
            if (quote.quoteRequest?.quoteRequestProject?.name) {
                const project = el.querySelector('.quote-project') as HTMLElement;
                project.textContent = quote.quoteRequest.quoteRequestProject.name;
            } else {
                projectContainer.parentElement?.removeChild(projectContainer);
            }

            const quoteLink = el.querySelector('#view-quote') as HTMLAnchorElement;
            quoteLink.addEventListener('click', async () => {
                const targetForBgColor = document.querySelector(`#${quote.id || ''}`) as HTMLElement;
                const otherQuotesForBgColor: HTMLElement[] = Array.from(document.querySelectorAll('.target-for-bg'));
                for (const div of otherQuotesForBgColor) {
                    if (div.id !== quote.id) {
                        div.classList.remove('bg-primary', 'bg-opacity-5');
                    }
                }
                targetForBgColor.classList.add('bg-primary', 'bg-opacity-5');
                const leftCol = document.querySelector('#left-column') as HTMLElement;
                leftCol.classList.add('col-lg-8');
                await axios.get<hyejoo.Quote>(`/quotes/${quote.id || ''}`).then(async resp => {
                    const currentQuote = resp.data.quote;
                    if (currentQuote) {
                        const quotePreviewContainer = document.querySelector('#quote-preview-container') as HTMLElement;
                        quotePreviewContainer.classList.remove('d-none');
                        if (currentQuote.quoteProofs?.length) {
                            const proofEls: HTMLElement[] = [];
                            const proofContainer = document.querySelector('#proof-container') as HTMLElement;
                            proofContainer.innerHTML = '';
                            const prevNextBtns = document.querySelector('#previous-next-btns') as HTMLElement;
                            if (currentQuote.quoteProofs.length === 1) {
                                prevNextBtns.classList.add('d-none');
                            } else {
                                prevNextBtns.classList.remove('d-none');
                            }
                            for (const op of currentQuote.quoteProofs) {
                                const prevBtn = document.querySelector('#previous-proof-btn') as HTMLElement;
                                const nextBtn = document.querySelector('#next-proof-btn') as HTMLElement;

                                const templateEl = template.Clone('#proofs-template');
                                const templateElContainer = templateEl.firstElementChild as HTMLElement;
                                templateElContainer.id = op.id || '';
                                proofEls.push(templateElContainer);
                                const name = op.name;
                                const proofImg = templateEl.querySelector('#proof-image') as HTMLImageElement;
                                const thumbAnchor = templateEl.querySelector('#proof-anchor') as HTMLAnchorElement;

                                const urls = GetProofURLs(window.cloudFront, op.proof?.file || '', op.proof?.path || '');

                                thumbAnchor.href = urls.main;
                                thumbAnchor.dataset.caption = `Proof ${name || ''}`;
                                thumbAnchor.setAttribute('data-fancybox', currentQuote.id || '');

                                proofImg.src = urls.thumb;
                                proofImg.alt = `Proof ${name || ''}`;
                                proofImg.title = `Proof ${name || ''}`;

                                proofContainer.appendChild(templateElContainer);

                                let currProof = 0;

                                const showHideProofs = () => {
                                    for (let i = 0; i < proofEls.length; i++) {
                                        if (i === currProof) {
                                            proofEls[i].classList.remove('d-none');
                                        } else {
                                            proofEls[i].classList.add('d-none');
                                        }
                                    }
                                };
                                nextBtn.addEventListener('click', () => {
                                    currProof++;
                                    if (currProof >= proofEls.length) {
                                        currProof = 0;
                                    }
                                    showHideProofs();
                                });
                                prevBtn.addEventListener('click', () => {
                                    currProof--;
                                    if (currProof < 0) {
                                        currProof = proofEls.length - 1;
                                    }
                                    showHideProofs();
                                });
                                showHideProofs();
                            }
                        }

                        const ordersLink = document.querySelector('#quote-ordered') as HTMLElement;
                        if (currentQuote.quoteOrders?.length) {
                            ordersLink.classList.remove('d-none');
                        } else {
                            ordersLink.classList.add('d-none');
                        }

                        const viewDetailsBtn = document.querySelector('#view-details-btn') as HTMLAnchorElement;
                        const orderNowBtn = document.querySelector('#order-now-btn') as HTMLAnchorElement;
                        const quoteNumber = document.querySelector('#quote-number') as HTMLAnchorElement;
                        quoteNumber.textContent = currentQuote.serial || '';
                        viewDetailsBtn.href = `${Relref('/quote')}?q=${currentQuote.key || ''}`;
                        orderNowBtn.addEventListener('click', async () => {
                            buttons.Start(orderNowBtn);
                            await carts.AddCartItem({ quoteID: currentQuote.key ?? '' }, {
                                promptToCheckout: false,
                            }).then(() => {
                                orderNowBtn.classList.remove('btn-primary', 'btn-success', 'text-white');
                                orderNowBtn.classList.add('btn-outline-success');

                                orderNowBtn.querySelector('i')?.classList.remove('fa-shopping-cart');
                                orderNowBtn.querySelector('i')?.classList.add('fa-check');
                            }).finally(() => {
                                buttons.Stop(orderNowBtn);
                                window.location.href = Relref('/cart');
                            });
                        });

                        const detailsContainer = document.querySelector('#details-container') as HTMLElement;
                        detailsContainer.innerHTML = '';
                        const table = await lineitems.Table(currentQuote.quoteLineItems || []);
                        detailsContainer.append(table);
                    }
                });
            });

            return el;
        },
    });

    pl.Load();
}